import { useCallback, useEffect, useRef, useState } from 'react';

import { extractionApi } from '../../api/extraction.js';
import { pipelineApi } from '../../api/pipeline.js';
import { PipelineProgress } from '../../components/pipeline/PipelineProgress.jsx';
import { ErrorState, MessageBanner } from '../../components/ui';
import { useUrlState } from '../../url';
import { BatchesTable } from './BatchesTable.jsx';
import { ExtractionForm } from './ExtractionForm.jsx';
import { ProcessingTable } from './ProcessingTable.jsx';
import { ReprocessResult } from './ReprocessResult.jsx';
import { ScanPanel } from './ScanPanel.jsx';
import { usePipelinePoll } from './usePipelinePoll.js';

const ACTIVE_POLL_MS = 15_000;

// Why quick reprocess refused, in ops language. Keys match the runner's
// `reason` field on status='requires_full' summaries.
const REQUIRES_FULL_MESSAGES = {
  rules_changed_since_last_full:
    'Billing rules changed since the last full reprocess — rule changes affect '
    + 'all records, so run a full Reprocess Billing.',
  netsuite_synced_since_last_full:
    'NetSuite reference data was synced since the last full reprocess — run a '
    + 'full Reprocess Billing to apply it everywhere.',
  no_successful_full_run:
    'No successful full reprocess found yet — run a full Reprocess Billing first.',
  no_watermarked_run:
    'The last full reprocess predates the quick-reprocess feature — run a full '
    + 'Reprocess Billing once to establish the change-tracking watermark.',
  adls_loader_running:
    'A data load is currently running — try Quick Reprocess again in a few minutes.',
};

export default function Extraction() {
  // Hydrated from the URL so "run the extract for this range" is a link, then
  // mirrored back one-way by the effect below. Deliberately NOT a URL-backed
  // useState: `reload()` is memoised with an empty dep list and updates these
  // via functional setters, and a setter whose identity changed on every URL
  // write would put that memo — and the effect that calls it — in a loop.
  const { query, setParams } = useUrlState();
  const [startDate, setStartDate] = useState(() => query.start || '');
  const [endDate,   setEndDate]   = useState(() => query.end || '');

  useEffect(() => {
    setParams({ start: startDate || null, end: endDate || null });
  }, [startDate, endDate, setParams]);

  const [loading,     setLoading]     = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [reprocessResult,  setReprocessResult]  = useState(null);

  const [error, setError] = useState(null);
  const [msg,   setMsg]   = useState(null);

  const [batches,    setBatches]    = useState(null);
  const [watermark,  setWatermark]  = useState(null);
  const [scanResult, setScanResult] = useState(null);

  // Synchronous guard against double-click within a single render frame.
  // React state setters are async, so disabled-prop updates race with rapid
  // clicks; a ref flips immediately and is checked at the top of every
  // handler before any await.
  const inFlightRef = useRef(false);

  // What's currently running anywhere — pipeline / reprocess / scheduled
  // cron — populated by the idle poll and by 409 responses. While non-null
  // all four buttons are disabled and a yellow banner shows what's running.
  const [activeConflict, setActiveConflict] = useState(null);

  // Reprocess polling tick (declared as a callable ref so the 409 handoff
  // and the page-load hydration can both kick it off without duplicating
  // the polling loop in the reprocessBilling handler).
  const reprocessTickRef = useRef(null);

  const reload = useCallback(() => {
    extractionApi.getBatches().then(setBatches).catch(() => {});
    extractionApi.getWatermark().then(setWatermark).catch(() => {});
    extractionApi
      .getCachedScan()
      .then((cached) => {
        if (!cached?.earliest_date) return;
        setScanResult(cached);
        setStartDate((s) => s || cached.earliest_date);
        setEndDate((s) => s || cached.latest_date);
      })
      .catch(() => {});
  }, []);

  const handlePipelineDone = useCallback(
    (s) => {
      setLoading(false);
      inFlightRef.current = false;
      reload();
      if (s.status === 'succeeded') {
        setMsg({ type: 'success', text: 'Pipeline completed successfully. All 3 steps finished.' });
      } else {
        setMsg({ type: 'error', text: `Pipeline failed: ${s.error || 'Unknown error'}` });
      }
    },
    [reload],
  );

  const handleNsSyncDone = useCallback(
    (s) => {
      setSyncLoading(false);
      inFlightRef.current = false;
      reload();
      if (s.status === 'succeeded') {
        setMsg({ type: 'success', text: 'NetSuite sync completed successfully.' });
      } else {
        setMsg({ type: 'error', text: `NetSuite sync failed: ${s.error || 'Unknown error'}` });
      }
    },
    [reload],
  );

  const pipelinePoll = usePipelinePoll(handlePipelineDone);
  const nsSyncPoll   = usePipelinePoll(handleNsSyncDone);

  // ``usePipelinePoll`` returns a fresh object each render (``reset`` is a
  // new arrow function), so closing over ``pipelinePoll`` directly would
  // make every callback unstable and re-fire effects on every render. The
  // ref tracks the latest value while keeping the callback identity stable.
  const pipelinePollRef = useRef(pipelinePoll);
  pipelinePollRef.current = pipelinePoll;

  // Reusable reprocess-status poller — same loop is invoked by the
  // reprocessBilling handler after a successful kickoff, by the 409
  // handoff (when another tab/user has a reprocess in flight), and by
  // page-load hydration (when GET /pipeline/active reports an in-flight
  // reprocess and we want to resume polling without re-clicking).
  const startReprocessPolling = useCallback((runId) => {
    const POLL_MS = 10_000;
    // 60-min budget. Reprocess baseline is 6-12 min, but observed runs
    // can stretch to 22+ min under SQL DTU pressure. 60 min keeps the
    // poller alive across that range without showing the "taking longer"
    // banner on healthy-but-slow runs.
    const MAX_POLLS = 360;
    let polls = 0;
    let timer = null;
    let stopped = false;

    const cleanup = () => {
      stopped = true;
      if (timer) { clearTimeout(timer); timer = null; }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };

    const finalizeTerminal = (status) => {
      setReprocessResult(status);
      setReprocessLoading(false);
      inFlightRef.current = false;
      reload();
      const summary = status.summary || {};
      // Quick runs rebuild only a delta — their with_gaps counts just the
      // rebuilt records, so the toast uses the whole-table gap count from
      // gap_summary instead of claiming "no gaps remain" table-wide.
      const isQuick = summary.run_mode === 'quick';
      const tableGaps = summary.gap_summary?.gap_count || 0;
      if (status.status === 'success' && isQuick) {
        setMsg({
          type: 'success',
          text: tableGaps > 0
            ? `Quick reprocess completed — ${summary.total_records ?? 0} records rebuilt. ${tableGaps} gaps remain table-wide (see the Records page).`
            : `Quick reprocess completed — ${summary.total_records ?? 0} records rebuilt. No gaps remain.`,
        });
      } else if (status.status === 'success' && (summary.with_gaps || 0) > 0) {
        setMsg({
          type: 'info',
          text: summary.email_status === 'sent'
            ? (summary.email_scope === 'requester'
              // Named, not "you": the poller also serves runs adopted from
              // the 409 handoff, which another user (or the timer) started.
              ? `Reprocess finished with gaps. Gap summary email sent to ${summary.triggered_by || 'whoever started this run'}.`
              : 'Reprocess finished with gaps. Gap summary email sent to configured recipients.')
            : 'Reprocess finished with gaps. See the result panel and the Records page.',
        });
      } else if (status.status === 'success') {
        setMsg({ type: 'success', text: 'Reprocess completed — no gaps remain.' });
      } else if (status.status === 'skipped_upstream_not_ready') {
        setMsg({
          type: 'info',
          text: 'Reprocess was skipped because upstream data is not ready. Billing records were not changed.',
        });
      } else if (status.status === 'skipped_concurrent_run') {
        setMsg({
          type: 'info',
          text: 'Reprocess was skipped because another reprocess is already running.',
        });
      } else if (status.status === 'no_data') {
        setMsg({ type: 'info', text: 'No punches found — reprocess was not run.' });
      } else if (status.status === 'no_changes') {
        setMsg({
          type: 'success',
          text: 'Quick reprocess: nothing changed since the last reprocess — billing records are already up to date.',
        });
      } else if (status.status === 'requires_full') {
        setMsg({
          type: 'info',
          text: REQUIRES_FULL_MESSAGES[summary.reason]
            || 'Quick reprocess is unavailable right now — run a full Reprocess Billing instead.',
        });
      } else {
        setMsg({
          type: 'error',
          text: `Reprocess failed: ${summary.error || 'Check the audit log for details.'}`,
        });
      }
    };

    const isTerminal = (s) => [
      'success', 'failed', 'skipped_upstream_not_ready',
      'skipped_concurrent_run', 'no_data', 'no_changes', 'requires_full',
    ].includes(s);

    // Re-fetch when the tab regains focus, so a long-running reprocess
    // that finished while the tab was backgrounded still shows the
    // result without requiring a manual refresh.
    const onVisibility = async () => {
      if (stopped) return;
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      try {
        const s = await pipelineApi.getReprocessStatus(runId);
        if (s && isTerminal(s.status)) {
          finalizeTerminal(s);
          cleanup();
        } else {
          setReprocessResult(s);
        }
      } catch { /* swallow; the regular tick will retry */ }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }

    const tick = async () => {
      if (stopped) return;
      polls += 1;
      let status;
      try {
        status = await pipelineApi.getReprocessStatus(runId);
      } catch (e) {
        if (polls >= MAX_POLLS) {
          setReprocessLoading(false);
          inFlightRef.current = false;
          setError(`Lost connection polling reprocess status: ${e.message}`);
          cleanup();
          return;
        }
        timer = setTimeout(tick, POLL_MS);
        return;
      }

      setReprocessResult(status);

      if (isTerminal(status.status)) {
        finalizeTerminal(status);
        cleanup();
        return;
      }

      if (polls >= MAX_POLLS) {
        // Pre-give-up final check: race-protect against the runner
        // having just-finished while we were about to bail.
        try {
          const final = await pipelineApi.getReprocessStatus(runId);
          if (final && isTerminal(final.status)) {
            finalizeTerminal(final);
            cleanup();
            return;
          }
        } catch { /* fall through to give-up banner */ }
        setReprocessLoading(false);
        inFlightRef.current = false;
        setMsg({
          type: 'info',
          text: 'Reprocess is taking longer than expected. Check the Audit Log later for completion.',
        });
        cleanup();
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };

    // First poll slightly faster so "unknown → running" feels snappy.
    timer = setTimeout(tick, 3_000);
  }, [reload]);

  // Keep latest tick fn in a ref so the conflict-handoff and hydration
  // paths can call it without depending on the callback identity.
  reprocessTickRef.current = startReprocessPolling;

  // Route a 409 conflict (or an idle-poll detection of an in-flight run)
  // into the matching progress UI: the regular pipeline poller for type
  // 'pipeline', the reprocess poller for 'reprocess'. Scheduled cron jobs
  // stay surfaced via activeConflict only — we don't poll them per-run.
  const adoptConflict = useCallback((conflict, { rejected = false } = {}) => {
    setActiveConflict(conflict);
    if (!conflict) return;
    if (conflict.type === 'pipeline' && conflict.run_id) {
      // Hydrate the regular pipeline-progress panel. If a REPROCESS click
      // got 409'd into this branch, release its loading flags — nothing on
      // this path will ever reset them otherwise (stuck "Reprocessing…").
      setReprocessLoading(false);
      inFlightRef.current = false;
      setLoading(true);
      pipelinePollRef.current?.start(conflict.run_id);
      setMsg({
        type: 'info',
        text: rejected
          ? `${conflict.label || 'A pipeline'} is already running — showing its progress here. Your click was not retried.`
          : `${conflict.label || 'A pipeline'} is running — showing its progress here.`,
      });
    } else if (conflict.type === 'reprocess' && conflict.run_id) {
      setReprocessLoading(true);
      setReprocessResult({ run_id: conflict.run_id, status: 'running' });
      reprocessTickRef.current?.(conflict.run_id);
      setMsg({
        type: 'info',
        text: rejected
          ? 'A reprocess is already running — resuming polling on it. Your click was not retried.'
          : 'A reprocess is running — resuming polling on it.',
      });
    } else if (conflict.type === 'scheduled') {
      // Banner-only: no poller takes over, so any loading flags a rejected
      // click set must be released here or the buttons stick on
      // "Reprocessing…" and the idle poll (gated on !reprocessLoading)
      // stops — a dead page until reload.
      setReprocessLoading(false);
      inFlightRef.current = false;
      const eta = conflict.eta_minutes ? `, typical duration ${conflict.eta_minutes} min` : '';
      setMsg({
        type: 'info',
        text: rejected
          ? `${conflict.label} is running (started ${conflict.started_at} UTC${eta}). Your click was not retried — please wait until it finishes.`
          : `${conflict.label} is running (started ${conflict.started_at} UTC${eta}). Please wait until it finishes.`,
      });
    }
  }, []);

  // Common 409 handler — pulls ``conflict`` out of the structured error
  // body the backend sends and routes it through ``adoptConflict``.
  // Returns true if it was a 409 (caller should NOT also call setError).
  const handleConflict = useCallback((e) => {
    if (e?.status !== 409) return false;
    const conflict = e.body?.conflict || null;
    if (conflict) {
      adoptConflict(conflict, { rejected: true });
    } else {
      // 409 without a structured conflict body — fall back to the message.
      setMsg({ type: 'info', text: e.body?.detail || 'Another pipeline is already running.' });
    }
    return true;
  }, [adoptConflict]);

  // Initial load + page-reload hydration. After the standard reload, ask
  // the backend whether anything is running right now; if so, surface it
  // so the user doesn't see a blank page mid-run after a refresh.
  useEffect(() => {
    reload();
    pipelineApi.getActive()
      .then((res) => { if (res?.conflict) adoptConflict(res.conflict); })
      .catch(() => {});
  }, [reload, adoptConflict]);

  // Idle-poll the active endpoint every 15 s while no local run is in
  // flight. This is what disables the four buttons automatically when a
  // scheduled cron job kicks off (e.g. the 4:00 UTC ADLS loader).
  const idle = !loading && !syncLoading && !reprocessLoading;
  useEffect(() => {
    if (!idle) return undefined;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const res = await pipelineApi.getActive();
        if (!cancelled) setActiveConflict(res?.conflict || null);
      } catch {
        // Transient — leave whatever the last successful poll set.
      }
      if (!cancelled) timer = setTimeout(tick, ACTIVE_POLL_MS);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [idle]);

  const scanDayforce = async () => {
    setScanLoading(true); setMsg(null); setError(null);
    try {
      const res = await extractionApi.scanDayforce();
      setScanResult(res);
      if (res.earliest_date) setStartDate(res.earliest_date);
      if (res.latest_date)   setEndDate(res.latest_date);
      setMsg({ type: 'success', text: res.message });
    } catch (e) {
      setError(e.message);
    }
    setScanLoading(false);
  };

  const startPipelineFor = async (start, end) => {
    try {
      const res = await pipelineApi.start(start, end);
      pipelinePoll.start(res.run_id);
    } catch (e) {
      if (handleConflict(e)) {
        // Another pipeline is already running; we've handed off polling.
        return;
      }
      setError(e.message);
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  const extract = async () => {
    if (inFlightRef.current) return;
    // Defense in depth — the button is disabled for these, but never POST a
    // bad range (end < start) or a future date even if the guard is bypassed.
    const today = new Date().toISOString().slice(0, 10);
    if (!startDate || !endDate) return;
    if (endDate < startDate) {
      setError('End date cannot be earlier than start date.');
      return;
    }
    if (startDate > today || endDate > today) {
      setError('Dates cannot be in the future.');
      return;
    }
    if (!window.confirm(
      `Extract raw data from Dayforce for ${startDate} to ${endDate}? `
        + `This will overwrite any existing pay-summary rows for these dates.`,
    )) return;
    inFlightRef.current = true;
    setLoading(true); setError(null); setMsg(null); pipelinePoll.reset();
    await startPipelineFor(startDate, endDate);
  };

  const runIncremental = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true); setError(null); setMsg(null); pipelinePoll.reset();
    // Time-precise incremental: start at the UTC moment the last successful
    // incremental extract BEGAN minus the backend's visibility-lag overlap
    // (Dayforce's modified-since filter lags writes), end now. Re-running
    // seconds later still pulls anything modified since then — there is no
    // "already up to date" state. Loaders dedup, so overlap is free.
    const lastRun = watermark?.incremental_start || watermark?.last_run_started_at;
    let start;
    if (lastRun) {
      start = lastRun; // full UTC timestamp from the backend
    } else {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      start = d.toISOString().slice(0, 10); // first ever run: last 7 days
    }
    let end = new Date().toISOString().slice(0, 19); // now, UTC, second precision
    if (start >= end) {
      // Clock-skew / same-second double click: widen to the last minute.
      start = new Date(Date.now() - 60_000).toISOString().slice(0, 19);
    }
    await startPipelineFor(start, end);
  };

  const syncNS = async () => {
    if (inFlightRef.current) return;
    if (!window.confirm(
      'Sync NetSuite reference data now? This replaces all ns.* tables from NetSuite; '
        + 'user-set include/exclude flags are preserved only on ns.departments and df.labor_metrics.',
    )) return;
    inFlightRef.current = true;
    setSyncLoading(true); setMsg(null); setError(null); nsSyncPoll.reset();
    try {
      const res = await pipelineApi.startNsSync();
      nsSyncPoll.start(res.run_id);
    } catch (e) {
      if (handleConflict(e)) {
        setSyncLoading(false);
        return;
      }
      setError(e.message);
      setSyncLoading(false);
      inFlightRef.current = false;
    }
  };

  // Shared kickoff for both reprocess buttons — runMode 'full' | 'quick'.
  const fireReprocess = async (runMode) => {
    inFlightRef.current = true;
    setReprocessLoading(true);
    setMsg(null);
    setError(null);
    setReprocessResult(null);

    // Kick off: backend returns {run_id, status: "running"} immediately,
    // OR returns 409 with a conflict dict if another pipeline / reprocess
    // / scheduled batch is already running.
    let runId;
    try {
      const kickoff = await pipelineApi.reprocessBilling({ force: false, runMode });
      runId = kickoff.run_id;
      setReprocessResult({
        run_id: runId, status: 'running',
        mode: kickoff.mode, run_mode: kickoff.run_mode, force: kickoff.force,
      });
    } catch (e) {
      if (handleConflict(e)) {
        // adoptConflict handed off polling and showed a banner.
        return;
      }
      setError(e.message);
      setReprocessLoading(false);
      inFlightRef.current = false;
      return;
    }

    startReprocessPolling(runId);
  };

  const reprocessBilling = async () => {
    if (inFlightRef.current) return;
    if (!window.confirm(
      'Rebuild billing records from loaded Dayforce and NetSuite data? '
        + 'Safe to run — readiness check protects against stale upstream data.',
    )) return;
    await fireReprocess('full');
  };

  // Quick = low-friction by design: no confirm dialog. It only touches the
  // employees/weeks whose source data changed, refuses when reference data
  // changed, and reports 'no changes' when there is nothing to do.
  const quickReprocessBilling = async () => {
    if (inFlightRef.current) return;
    await fireReprocess('quick');
  };

  return (
    <div>
      <div className="section-title">Dayforce Data Extraction</div>

      <ScanPanel
        scanLoading={scanLoading}
        scanResult={scanResult}
        onScan={scanDayforce}
      />

      {activeConflict && (
        <div
          className="alert alert-warning"
          style={{
            background: '#fff8e1',
            border: '1px solid #f0c36d',
            color: '#7a4f00',
            padding: '10px 12px',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          <strong>{activeConflict.label || 'A pipeline'}</strong> is currently running
          {activeConflict.started_at ? <> (started {activeConflict.started_at} UTC)</> : null}
          {activeConflict.eta_minutes ? <> · typical duration {activeConflict.eta_minutes} min</> : null}
          . Please wait until it finishes — the buttons below are disabled until then.
        </div>
      )}

      <ExtractionForm
        startDate={startDate}   setStartDate={setStartDate}
        endDate={endDate}       setEndDate={setEndDate}
        onExtract={extract}
        onIncremental={runIncremental}
        onSyncNs={syncNS}
        onReprocess={reprocessBilling}
        onQuickReprocess={quickReprocessBilling}
        loading={loading}
        syncLoading={syncLoading}
        reprocessLoading={reprocessLoading}
        disabledByConflict={Boolean(activeConflict)}
      />

      {watermark?.last_extraction_end && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
          Last extraction: <strong>{watermark.last_extraction_end}</strong>
          {watermark.last_run_started_at && (
            <> (run started <strong>{watermark.last_run_started_at.replace('T', ' ')} UTC</strong>)</>
          )}
        </div>
      )}

      <PipelineProgress status={pipelinePoll.status} title="Data Extraction Pipeline" />
      <PipelineProgress status={nsSyncPoll.status}   title="NetSuite Sync Pipeline" />

      {loading && !pipelinePoll.status && (
        <div className="alert alert-info">Starting pipeline...</div>
      )}

      <ReprocessResult result={reprocessResult} />

      {error && <ErrorState>{error}</ErrorState>}
      <MessageBanner msg={msg} onDismiss={() => setMsg(null)} />

      {batches === null && <div className="alert alert-info">Loading batches...</div>}
      <BatchesTable batches={batches} />
      <ProcessingTable batches={batches} />
    </div>
  );
}
